<script setup lang="ts">
import { vIntersectionObserver } from '@vueuse/components'
import { computed, reactive, useDevice, useNuxtApp } from '#imports'
import type { SectionInfo } from '@game/composables/useGameSections'

const props = withDefaults(defineProps<{
    sections?: SectionInfo[]
    isLoading?: boolean
    hasSidebar?: boolean
    maxSections?: number
}>(), {
    sections: () => [],
    hasSidebar: false,
    isLoading: false,
})

const visibleSections = computed(() => {
    if (!props.sections?.length) return []
    return props.maxSections
        ? props.sections.slice(0, props.maxSections)
        : props.sections
})

// hydrate-on-visible can't defer client-side-nav mounts (no markup to hydrate).
// Gate only that case; isHydrating is true during hydration, so SSR is unaffected.
const nuxtApp = useNuxtApp()
const gateOnClientNav = import.meta.client && !nuxtApp.isHydrating

const revealedSlugs = reactive(new Set<string>())

function onSectionVisible(entries: IntersectionObserverEntry[]) {
    // Iterate: one dispatch can batch a [leave, enter] pair and drop the enter otherwise.
    for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const slug = (entry.target as HTMLElement).dataset.sectionSlug
        if (slug) revealedSlugs.add(slug)
    }
}

const { isMobile } = useDevice()

// Match GameCustomSection's rows/variant so the placeholder reserves the real
// height — otherwise the reveal shifts layout, the CLS this gate exists to avoid.
function inlineRows(section: SectionInfo) {
    if (!section.type?.startsWith('Horizontal')) return 0
    const lines = isMobile ? section.horizontalLines : (section.horizontalDesktopLines ?? section.horizontalLines)
    return lines || 1
}

function tileVariant(section: SectionInfo) {
    if (section.type === 'Horizontal-Taller') return 'taller'
    if (section.type === 'Horizontal-Tall') return 'tall'
    return undefined
}
</script>

<template>
    <div v-if="isLoading || visibleSections.length" class="dynamic-sections">
        <template v-if="isLoading && !visibleSections.length">
            <GameList
                category="dynamic-sections-skeleton"
                :games="[]"
                :has-sidebar="hasSidebar"
                :skeletons="8"
            />
        </template>

        <template v-else>
            <!-- hydrate-on-visible: the first section is on screen, so it still hydrates at once, in its own task. -->
            <LazyVisibleGameCustomSection
                v-if="visibleSections[0]"
                :key="visibleSections[0].slug"
                :slug="visibleSections[0].slug"
                :name="visibleSections[0].name"
                :type="visibleSections[0].type"
                :horizontal-lines="visibleSections[0].horizontalLines"
                :horizontal-desktop-lines="visibleSections[0].horizontalDesktopLines"
                :tile-display-count="visibleSections[0].tileDisplayCount"
                :contains-all-games="visibleSections[0].containsAllGames"
                :video-enabled="visibleSections[0].videoEnabled"
                :has-sidebar="hasSidebar"
                :lazy="false"
                hydrate-on-visible
            />

            <slot name="after-first" />

            <template v-for="section in visibleSections.slice(1)" :key="section.slug">
                <LazyVisibleGameCustomSection
                    v-if="!gateOnClientNav || revealedSlugs.has(section.slug)"
                    :slug="section.slug"
                    :name="section.name"
                    :type="section.type"
                    :horizontal-lines="section.horizontalLines"
                    :horizontal-desktop-lines="section.horizontalDesktopLines"
                    :tile-display-count="section.tileDisplayCount"
                    :contains-all-games="section.containsAllGames"
                    :video-enabled="section.videoEnabled"
                    :lazy="true"
                    hydrate-on-visible
                />

                <GameList
                    v-else
                    v-intersection-observer="[onSectionVisible, { rootMargin: '100px' }]"
                    :data-section-slug="section.slug"
                    category="dynamic-sections-skeleton"
                    :games="[]"
                    :title="section.name"
                    :has-sidebar="hasSidebar"
                    :inline-rows="inlineRows(section)"
                    :tile-variant="tileVariant(section)"
                    :skeletons="8"
                />
            </template>

            <slot name="after-all" />
        </template>
    </div>
</template>

<style lang="scss" scoped>
.dynamic-sections {
    display: flex;
    flex-direction: column;
    gap: 20px;

    @include tablet(md) {
        gap: 30px;
    }

    // Gap handles all spacing; prevent double-spacing from GameList's global sibling margins
    // (custom-game-section pairs, and skeleton placeholders rendered as direct children).
    :deep(.custom-game-section + .custom-game-section) {
        margin-top: 0;
    }

    & > :deep(.game-list__section) {
        margin-top: 0;
    }
}
</style>
